import Image from "next/image";
// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, so
// every card click paid a middleware redirect round-trip.
import { Link } from "@/i18n/routing";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";
import { LjAdBand } from "./ad-band";
import { AdSlot as GamAdSlot } from "@/components/ads/ad-slot";


/** Top-bordered uppercase sidebar section heading — shared by every listing/article sidebar. */
export function SidebarSectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="border-t pt-4"
      style={{ borderColor: LJ_COLORS.dividerStrong }}
    >
      <h2 style={{ ...LJ_TYPE.sectionTitle, color: LJ_COLORS.textPrimary }}>
        {children}
      </h2>
    </div>
  );
}

/**
 * Sidebar "Advertisement" placeholder — shared by article + blog-detail sidebars.
 *
 * `sticky` pins THIS unit while the rest of the rail scrolls past, the same
 * shape the main site uses (`components/shared/main/ads.tsx`). Both LJ detail
 * pages pinned the ENTIRE rail instead — a 1,800px sticky block in a 900px
 * viewport barely moves, so the unit at its foot was never on screen when it
 * mattered. QA: "similar to the main hkej, the last ad unit on the sidebar
 * should be sticky". Desktop only: below `lg` the rail is a full-width band
 * under the article, where sticky would park the creative over the text.
 */
/** Phone only. Desktop and tablet size lists stay empty, so this element
 *  takes a LargeRectangle number on a phone and nowhere else. */
const PHONE_LREC_SIZES = { desktop: [], tablet: [] } as const;

export function PhoneLrec({ className = "" }: { className?: string }) {
  return (
    <div className={`mx-auto w-full max-w-[300px] md:hidden ${className}`}>
      <LjAdBand>
        <GamAdSlot unit="LargeRectangle" sizes={PHONE_LREC_SIZES} className="w-full">
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: "300 / 250" }}>
            <Image
              src="/images/lj/topics/cboe-ad-banner.png"
              alt=""
              fill
              className="object-contain object-center"
              sizes="300px"
              unoptimized
            />
          </div>
        </GamAdSlot>
      </LjAdBand>
    </div>
  );
}

export function SidebarAd({
  src,
  aspectRatio,
  sticky = false,
  href,
  className = "mx-auto w-full max-w-[300px]",
}: {
  src: string;
  aspectRatio: string;
  sticky?: boolean;
  /** Click-through of the fallback creative (plain image without one). */
  href?: string;
  className?: string;
}) {
  const frame = (
    <div className="relative w-full overflow-hidden" style={{ aspectRatio }}>
      <Image src={src} alt="Sidebar advertisement" fill className="object-contain object-center" sizes="300px" unoptimized />
    </div>
  );
  return (
    <LjAdBand
      className={`${className}${
        sticky ? " lg:sticky hkej-sticky-offset" : ""
      }`}
    >
      <GamAdSlot unit={isTallRatio(aspectRatio) ? "HalfPage" : "LargeRectangle"} className="w-full">
        {href ? (
          <Link href={href} className="block w-full" aria-label="Advertisement">
            {frame}
          </Link>
        ) : (
          frame
        )}
      </GamAdSlot>
    </LjAdBand>
  );
}

export type SidebarArticle = {
  category: string;
  title: string;
  href: string;
  image: string;
};

/**
 * Thumbnail + category + title row used in the article "Popular" rail and the
 * blog-detail "More from {author}" list. The two call sites differ only in a
 * few orthogonal toggles, kept as literal class strings so each renders
 * byte-identically to its previous inline copy.
 */
export function SidebarArticleItem({
  item,
  innerGap,
  fullWidth = false,
  clampTitle = false,
}: {
  item: SidebarArticle;
  innerGap: "1" | "1.5";
  fullWidth?: boolean;
  clampTitle?: boolean;
}) {
  const linkClass = fullWidth
    ? "group hkej-tap flex w-full items-start gap-3"
    : "group hkej-tap flex items-start gap-3";
  const innerClass =
    innerGap === "1.5"
      ? "flex min-w-0 flex-1 flex-col justify-center gap-1.5"
      : "flex min-w-0 flex-1 flex-col justify-center gap-1";

  return (
    <Link href={item.href} className={linkClass}>
      <div className={innerClass}>
        <span
          style={{
            ...LJ_TYPE.eyebrow,
            fontSize: 11,
            fontWeight: 400,
            letterSpacing: "0.14em",
            color: LJ_COLORS.textPrimary,
          }}
        >
          {item.category}
        </span>
        <h4
          className={
            clampTitle ? "hkej-tap-title line-clamp-2" : "hkej-tap-title"
          }
          style={{
            ...LJ_TYPE.cardHedSm,
            fontSize: 15,
            lineHeight: "20px",
            color: LJ_COLORS.textPrimary,
          }}
        >
          {item.title}
        </h4>
      </div>
      <div
        className="relative shrink-0 overflow-hidden"
        style={{ width: 96, aspectRatio: "4 / 3" }}
      >
        <Image
          src={item.image}
          alt={item.title}
          fill
          className="object-contain object-center"
          sizes="96px"
          unoptimized
        />
      </div>
    </Link>
  );
}

/** "300/600"-shaped creatives are the HalfPage unit; everything else an LREC. */
function isTallRatio(ratio: string): boolean {
  const [w, h] = ratio.split("/").map((n) => parseFloat(n));
  return Boolean(w && h) && h / w > 1.6;
}

/**
 * The magazine's in-page wide banner (summary grid, tablet+): a BigBanner unit numbered in
 * DOM order (BigBanner1 on a listing, BigBanner2 on home after the super banner). It used to be a
 * LongBanner, so it came back empty once the page-top creative had served. `fitOversize` scales a
 * 970 creative into a narrower column instead of cropping.
 */
export function LjMidBanner({ src, href, unit = "BigBanner", className = "" }: { src: string; href?: string; unit?: string; className?: string }) {
  const img = (
    <Image src={src} alt="Advertisement" width={1024} height={169} className="h-auto w-full" sizes="100vw" unoptimized />
  );
  return (
    <LjAdBand className={className}>
      <GamAdSlot unit={unit} className="w-full" fitOversize>
        {href ? (
          <Link href={href} className="block w-full" aria-label="Advertisement">
            {img}
          </Link>
        ) : (
          <div className="block w-full">{img}</div>
        )}
      </GamAdSlot>
    </LjAdBand>
  );
}
